import React, { useEffect, useMemo, useState } from 'react';
import {
  Header,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
  TableProps,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { SlaExceptionRow, SlaVerdict } from '../../../interfaces/panel-data';
import { formatDateFromDate, formatTimeFromDate } from '../../../utils/date-helper';
import { formatDuration } from '../../../utils/format-duration';
import { Badge, BadgeVariant } from '../Badge';
import { VERDICT_LABELS } from './sla-verdict-labels';
import './SlaExceptionTable.scss';

const PAGE_SIZE = 20;

/** Stands in for every absent value in the table, so a blank cell is never a value in its own right. */
const ABSENT = '—';

/** The sentinel for "every group", kept out of the key namespace a stage or leg code could occupy. */
const ALL_GROUPS = '*';

/**
 * What the breaching-only filter keeps. Narrower than "is an exception": the payload's exception list also
 * carries rows that are stale or unregistered, and neither is a budget overrun — a reader chasing breaches
 * should not have to tell them apart by eye.
 */
const BREACHING_VERDICTS: SlaVerdict[] = ['BREACHED', 'BREACHING'];

/** `neutral` for UNMEASURED, because nothing was judged — see the same rule in `SlaPanel`. */
const VERDICT_VARIANTS: Record<SlaVerdict, BadgeVariant> = {
  WITHIN_SLA: 'success',
  AT_RISK: 'warning',
  BREACHING: 'error',
  BREACHED: 'error',
  NOT_REGISTERED: 'warning',
  STALE: 'warning',
  UNMEASURED: 'neutral',
};

const duration = (milliseconds: number | null): string =>
  milliseconds === null ? ABSENT : formatDuration(milliseconds);

const timestamp = (milliseconds: number): string => {
  const at = new Date(milliseconds);
  return `${formatDateFromDate(at)} ${formatTimeFromDate(at)}`;
};

/**
 * Declared once, outside the definition builder, so the default sorting state can point at this exact
 * object — collection hooks match the sorting column by reference.
 *
 * Null sorts below every real margin rather than being skipped, which under the default descending order
 * puts the rows with no budget at the end. They are in the list because they are unregistered or stale, not
 * because they overran, so they belong after everything that did.
 */
const OVER_BY_COLUMN: TableProps.ColumnDefinition<SlaExceptionRow> = {
  id: 'overBy',
  header: 'Over by',
  cell: (row) => duration(row.overByMs),
  sortingComparator: (a, b) => (a.overByMs ?? -1) - (b.overByMs ?? -1),
};

/**
 * `groupNames` rather than the raw key, so no stage or leg code reaches the screen; `groupHeader` because
 * the batch tier's groups are stages and the delta tier's are legs, and calling both "Group" would make the
 * column mean less than it costs.
 */
const columnDefinitions = (
  showWebsite: boolean,
  groupNames: Record<string, string>,
  groupHeader: string
): Array<TableProps.ColumnDefinition<SlaExceptionRow>> => [
  { id: 'id', header: 'Record', cell: (row) => row.id, sortingField: 'id' },
  ...(showWebsite
    ? [
        {
          id: 'website',
          header: 'Website',
          cell: (row: SlaExceptionRow) => row.website ?? ABSENT,
          sortingField: 'website',
        },
      ]
    : []),
  {
    id: 'group',
    header: groupHeader,
    cell: (row) => groupNames[row.groupKey] ?? row.groupKey,
    sortingField: 'groupKey',
  },
  { id: 'status', header: 'Status', cell: (row) => row.status ?? ABSENT, sortingField: 'status' },
  {
    id: 'elapsed',
    header: 'Elapsed',
    // A row that stopped being re-ingested while still open ran *at least* this long, so the figure is
    // marked as the bound it is rather than printed as if the clock had been read at the end.
    cell: (row) => `${row.elapsedIsLowerBound ? '≥ ' : ''}${formatDuration(row.elapsedMs)}`,
    sortingField: 'elapsedMs',
  },
  { id: 'budget', header: 'Budget', cell: (row) => duration(row.budgetMs), sortingField: 'budgetMs' },
  OVER_BY_COLUMN,
  {
    id: 'verdict',
    header: 'Verdict',
    cell: (row) => <Badge variant={VERDICT_VARIANTS[row.verdict]}>{VERDICT_LABELS[row.verdict]}</Badge>,
    sortingField: 'verdict',
  },
  {
    id: 'lastSeen',
    header: 'Last seen',
    cell: (row) => timestamp(row.lastObservedAtMs),
    sortingField: 'lastObservedAtMs',
  },
];

interface SlaExceptionTableProps {
  heading: string;
  rows: SlaExceptionRow[];
  /** Pre-cap, so the note below the table can say how many rows were withheld rather than implying none. */
  exceptionTotal: number;
  /** Rendered verbatim, and only when the cap actually withheld rows. */
  capNote?: string;
  /** COHORT only: in website scope every row is the one website's, so the column would repeat itself. */
  showWebsite: boolean;
  groupNames: Record<string, string>;
  groupHeader: string;
}

/**
 * The ranked list behind both tiers: which individual records are overrunning, worst margin first.
 *
 * Sorted, filtered and paged in the browser. The backend cannot help with any of the three — the accessor
 * takes a single field name with a hardcoded `DESC` and no page parameter — but it has already ranked and
 * capped the rows by margin, so what arrives is the worst of the population rather than an arbitrary page
 * of it.
 *
 * The breaching filter starts on. The list is short enough to read only because it is filtered, and a
 * reader who opens this table is asking what is over budget; stale and unregistered rows answer a different
 * question and are one click away.
 */
export const SlaExceptionTable: React.FC<SlaExceptionTableProps> = ({
  heading,
  rows,
  exceptionTotal,
  capNote,
  showWebsite,
  groupNames,
  groupHeader,
}) => {
  const [breachingOnly, setBreachingOnly] = useState(true);
  const [groupKey, setGroupKey] = useState(ALL_GROUPS);

  const columns = useMemo(
    () => columnDefinitions(showWebsite, groupNames, groupHeader),
    [showWebsite, groupNames, groupHeader]
  );

  // Filtered before the hook rather than through its `filteringFunction`, which is built around a text box
  // this table does not have.
  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!breachingOnly || BREACHING_VERDICTS.includes(row.verdict)) &&
          (groupKey === ALL_GROUPS || row.groupKey === groupKey)
      ),
    [rows, breachingOnly, groupKey]
  );

  const { items, collectionProps, paginationProps, actions } = useCollection(visible, {
    sorting: { defaultState: { sortingColumn: OVER_BY_COLUMN, isDescending: true } },
    pagination: { pageSize: PAGE_SIZE },
  });

  // Built from the rows on screen rather than from the tier's groups: an option that filters to nothing is
  // a dead end, and the cap means a group can be configured, measured, and still absent from this list.
  //
  // "On screen" has to mean after the breaching filter too, not just after the cap. With the filter on — its
  // default — a group of nothing but stale and unregistered rows would otherwise still be offered, and
  // picking it lands on "No record matches these filters.": the dead end this list is built to avoid. Not
  // derived from `visible`, which also filters by `groupKey` and would collapse the list to the one group
  // already selected.
  const groupOptions: SelectProps.Option[] = useMemo(() => {
    const candidates = breachingOnly
      ? rows.filter((row) => BREACHING_VERDICTS.includes(row.verdict))
      : rows;
    const keys = Array.from(new Set(candidates.map((row) => row.groupKey)));
    return [
      { value: ALL_GROUPS, label: `All ${groupHeader.toLowerCase()}s` },
      ...keys.map((key) => ({ value: key, label: groupNames[key] ?? key })),
    ];
  }, [rows, breachingOnly, groupNames, groupHeader]);

  // `groupKey` outlives the options it was chosen from: the toggle above narrows them, and a re-fetch on a
  // window change re-renders this table in place with a new `rows`. Either can retire the selected group
  // while the state still holds it, and then `selectedGroup` falls back to "All …s" while `visible` goes on
  // filtering for a key nothing matches — an empty table under a control saying it shows everything.
  useEffect(() => {
    if (groupKey !== ALL_GROUPS && !groupOptions.some((option) => option.value === groupKey)) {
      setGroupKey(ALL_GROUPS);
      actions.setCurrentPage(1);
    }
  }, [groupOptions, groupKey, actions]);

  const selectedGroup = groupOptions.find((option) => option.value === groupKey) ?? groupOptions[0];

  return (
    <div className="ovp-sla-exceptions">
      <Table
        {...collectionProps}
        data-testid="sla-exception-table"
        columnDefinitions={columns}
        items={items}
        trackBy="id"
        variant="embedded"
        header={<Header variant="h3" counter={`(${visible.length})`}>{heading}</Header>}
        filter={
          <SpaceBetween direction="horizontal" size="m">
            <Toggle
              checked={breachingOnly}
              onChange={({ detail }) => {
                setBreachingOnly(detail.checked);
                actions.setCurrentPage(1);
              }}
              data-testid="sla-exception-breaching-only"
            >
              Breaching only
            </Toggle>
            <Select
              selectedOption={selectedGroup}
              options={groupOptions}
              onChange={({ detail }) => {
                setGroupKey(detail.selectedOption.value ?? ALL_GROUPS);
                actions.setCurrentPage(1);
              }}
              data-testid="sla-exception-group-filter"
            />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        empty="No record matches these filters."
      />
      {/* R12: an uncapped list carries no note, because a note that is always there stops being read. */}
      {capNote && exceptionTotal > rows.length && (
        <p className="ovp-sla-exceptions__cap-note" data-testid="sla-exception-cap-note">
          {capNote}
        </p>
      )}
    </div>
  );
};
